import React, { useEffect, useState, useRef } from 'react';
import { Palette, Type, Search, Save, RotateCcw, Upload, Info, AlertTriangle, Check, Globe, Copy, Trash2, Download, FileUp, Plus, Heart } from 'lucide-react';
import { useApi } from './DashboardLayout';
import { API } from '../App';

const COLOR_SECTIONS = [
  {
    title: 'Hauptfarben',
    items: [
      { key: 'primary', label: 'Primärfarbe (Logo/Buttons)', desc: 'Hauptfarbe der App (BGPR Rot)' },
      { key: 'primaryHover', label: 'Primärfarbe Hover', desc: 'Beim Überfahren mit der Maus' },
      { key: 'secondary', label: 'Sekundärfarbe', desc: 'Dunkle Akzentfarbe' },
    ],
  },
  {
    title: 'Sidebar',
    items: [
      { key: 'sidebarBg', label: 'Hintergrund', desc: 'Sidebar-Hintergrundfarbe' },
      { key: 'sidebarText', label: 'Text', desc: 'Menüpunkte (inaktiv)' },
      { key: 'sidebarActive', label: 'Aktiver Menüpunkt', desc: 'Hintergrund des aktiven Menüs' },
    ],
  },
  {
    title: 'Seitenbereiche',
    items: [
      { key: 'headerBg', label: 'Header-Hintergrund', desc: 'Obere Leiste' },
      { key: 'pageBg', label: 'Seiten-Hintergrund', desc: 'Hauptbereich der Seite' },
      { key: 'cardBg', label: 'Karten-Hintergrund', desc: 'Weiße Boxen/Karten' },
    ],
  },
  {
    title: 'Text',
    items: [
      { key: 'textPrimary', label: 'Haupttext', desc: 'Überschriften und wichtiger Text' },
      { key: 'textSecondary', label: 'Sekundärtext', desc: 'Beschreibungen und Hinweise' },
    ],
  },
  {
    title: 'Status-Farben',
    items: [
      { key: 'success', label: 'Erfolg', desc: 'Bestätigungen, Aktive Status' },
      { key: 'warning', label: 'Warnung', desc: 'Hinweise, Ablaufende Lizenzen' },
      { key: 'error', label: 'Fehler', desc: 'Fehlermeldungen, Abgelaufene Lizenzen' },
      { key: 'info', label: 'Info', desc: 'Informationen, Links' },
    ],
  },
  {
    title: 'Button-Farben (Hintergrund)',
    items: [
      { key: 'btnPrimaryBg', label: 'Primär-Button', desc: 'Hauptaktionen (Erstellen, Speichern)' },
      { key: 'btnEditBg', label: 'Bearbeiten-Button', desc: 'Bearbeiten-Aktionen' },
      { key: 'btnExtendBg', label: 'Verlängern-Button', desc: 'Lizenzverlängerung' },
      { key: 'btnDangerBg', label: 'Gefahr-Button', desc: 'Löschen, Deaktivieren' },
    ],
  },
  {
    title: 'Button-Farben (Schrift)',
    items: [
      { key: 'btnPrimaryText', label: 'Primär-Button Schrift', desc: 'Textfarbe für Hauptaktionen' },
      { key: 'btnEditText', label: 'Bearbeiten-Button Schrift', desc: 'Textfarbe für Bearbeiten' },
      { key: 'btnExtendText', label: 'Verlängern-Button Schrift', desc: 'Textfarbe für Verlängerung' },
      { key: 'btnDangerText', label: 'Gefahr-Button Schrift', desc: 'Textfarbe für Löschen, Deaktivieren' },
    ],
  },
];

const DEFAULT_COLORS = {
  primary: '#CC0000', primaryHover: '#990000', secondary: '#1F2937',
  sidebarBg: '#1F2937', sidebarText: '#9CA3AF', sidebarActive: '#CC0000',
  headerBg: '#FFFFFF', pageBg: '#F3F4F6', cardBg: '#FFFFFF',
  textPrimary: '#111827', textSecondary: '#6B7280',
  success: '#10B981', warning: '#F59E0B', error: '#EF4444', info: '#3B82F6',
  btnPrimaryBg: '#CC0000', btnEditBg: '#1F2937', btnExtendBg: '#2563EB', btnDangerBg: '#DC2626',
  btnPrimaryText: '#FFFFFF', btnEditText: '#FFFFFF', btnExtendText: '#FFFFFF', btnDangerText: '#FFFFFF',
};

export const FONT_OPTIONS = [
  { id: 'Titillium Web', label: 'Titillium Web', sample: 'ABCDEFGHIJ abcdefghij 0123456789' },
  { id: 'system', label: 'System Standard', sample: 'ABCDEFGHIJ abcdefghij 0123456789' },
  { id: 'Arial', label: 'Arial', sample: 'ABCDEFGHIJ abcdefghij 0123456789' },
  { id: 'Helvetica', label: 'Helvetica', sample: 'ABCDEFGHIJ abcdefghij 0123456789' },
  { id: 'Georgia', label: 'Georgia', sample: 'ABCDEFGHIJ abcdefghij 0123456789' },
  { id: 'Times New Roman', label: 'Times New Roman', sample: 'ABCDEFGHIJ abcdefghij 0123456789' },
  { id: 'Verdana', label: 'Verdana', sample: 'ABCDEFGHIJ abcdefghij 0123456789' },
  { id: 'Tahoma', label: 'Tahoma', sample: 'ABCDEFGHIJ abcdefghij 0123456789' },
  { id: 'Trebuchet MS', label: 'Trebuchet MS', sample: 'ABCDEFGHIJ abcdefghij 0123456789' },
  { id: 'Courier New', label: 'Courier New', sample: 'ABCDEFGHIJ abcdefghij 0123456789' },
];

const ROBOTS_OPTIONS = [
  { value: 'noindex, nofollow', label: 'noindex, nofollow', desc: 'Nicht indexieren, Links nicht folgen (Empfohlen)' },
  { value: 'noindex, follow', label: 'noindex, follow', desc: 'Nicht indexieren, aber Links folgen' },
  { value: 'index, nofollow', label: 'index, nofollow', desc: 'Indexieren, aber Links nicht folgen' },
  { value: 'index, follow', label: 'index, follow', desc: 'Vollständig indexieren (Öffentliche Seite)' },
];

function applyColors(colors) {
  const root = document.documentElement;
  const map = {
    primary: '--primary', primaryHover: '--primary-hover', secondary: '--secondary',
    sidebarBg: '--sidebar-bg', sidebarText: '--sidebar-text', sidebarActive: '--sidebar-active',
    headerBg: '--header-bg', pageBg: '--page-bg', cardBg: '--card-bg',
    textPrimary: '--text-primary', textSecondary: '--text-secondary',
    success: '--success', warning: '--warning', error: '--error', info: '--info',
    btnPrimaryBg: '--primary', btnEditBg: '--btn-edit', btnExtendBg: '--btn-extend', btnDangerBg: '--btn-danger',
  };
  Object.entries(map).forEach(([key, varName]) => {
    if (colors[key]) root.style.setProperty(varName, colors[key]);
  });
}

function applyFont(font) {
  const family = font === 'system' ? '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' : `'${font}', sans-serif`;
  document.body.style.fontFamily = family;
}

export { applyColors, applyFont };

/* ============ COLOR ROW ============ */
function ColorRow({ item, value, defaultValue, onChange }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <div className="design-color-row" data-testid={`color-${item.key}`}>
      <div
        className="design-color-swatch"
        style={{ background: value }}
        onClick={() => setPickerOpen(!pickerOpen)}
      >
        {pickerOpen && (
          <input
            type="color"
            value={value}
            onChange={e => onChange(item.key, e.target.value)}
            onBlur={() => setPickerOpen(false)}
            autoFocus
            style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
          />
        )}
      </div>
      <div className="design-color-info">
        <span className="design-color-label">{item.label}</span>
        <span className="design-color-desc">{item.desc}</span>
      </div>
      <input
        className="design-color-hex"
        value={value}
        onChange={e => onChange(item.key, e.target.value)}
        data-testid={`color-input-${item.key}`}
      />
      <button
        className="design-color-reset"
        onClick={() => onChange(item.key, defaultValue)}
        title="Zurücksetzen"
        data-testid={`color-reset-${item.key}`}
      >
        <RotateCcw size={14} />
      </button>
    </div>
  );
}

/* ============ LIVE PREVIEW ============ */
function LivePreview({ colors }) {
  return (
    <div className="design-preview-box" data-testid="live-preview">
      <div style={{ display: 'flex', height: 140, borderRadius: 6, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
        {/* Mini sidebar */}
        <div style={{ width: 50, background: colors.sidebarBg, padding: '8px 4px', display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ height: 6, borderRadius: 2, background: colors.sidebarActive, margin: '0 2px' }} />
          <div style={{ height: 6, borderRadius: 2, background: colors.sidebarText, opacity: 0.4, margin: '0 2px' }} />
          <div style={{ height: 6, borderRadius: 2, background: colors.sidebarText, opacity: 0.4, margin: '0 2px' }} />
        </div>
        {/* Mini content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: 20, background: colors.headerBg, borderBottom: '1px solid #e5e7eb' }} />
          <div style={{ flex: 1, background: colors.pageBg, padding: 6, display: 'flex', gap: 4 }}>
            <div style={{ flex: 1, background: colors.cardBg, borderRadius: 4, padding: 6 }}>
              <div style={{ height: 5, width: '60%', background: colors.textPrimary, borderRadius: 2, marginBottom: 4 }} />
              <div style={{ height: 4, width: '80%', background: colors.textSecondary, borderRadius: 2, marginBottom: 6, opacity: 0.5 }} />
              <div style={{ display: 'flex', gap: 3 }}>
                <div style={{ height: 12, width: 28, background: colors.btnPrimaryBg, borderRadius: 3 }} />
                <div style={{ height: 12, width: 28, background: colors.btnEditBg, borderRadius: 3 }} />
              </div>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ flex: 1, background: colors.success, borderRadius: 4, opacity: 0.3 }} />
              <div style={{ flex: 1, background: colors.warning, borderRadius: 4, opacity: 0.3 }} />
              <div style={{ flex: 1, background: colors.error, borderRadius: 4, opacity: 0.3 }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============ MAIN COMPONENT ============ */
export default function DesignPage({ onTitleChange }) {
  const [tab, setTab] = useState('farben');
  const [colors, setColors] = useState({ ...DEFAULT_COLORS });
  const [savedColors, setSavedColors] = useState({ ...DEFAULT_COLORS });
  const [font, setFont] = useState('Titillium Web');
  const [savedFont, setSavedFont] = useState('Titillium Web');
  const [seo, setSeo] = useState({ title: 'BGPR Verwaltung', description: 'Verwaltungssystem für Herzsportgruppen', keywords: 'herzsport, bgpr, übungsleiter', robots: 'noindex, nofollow', customCss: '' });
  const [logoPath, setLogoPath] = useState(null);
  const [pdfLogoPath, setPdfLogoPath] = useState(null);
  const [siteUrl, setSiteUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [fixNotify, setFixNotify] = useState({ enabled: false, email: '' });
  const { apiFetch } = useApi();

  useEffect(() => {
    loadConfig();
    loadLogo();
    loadSiteUrl();
    loadFixNotify();
  }, []);

  const loadConfig = async () => {
    const r = await apiFetch('/api/settings/design-config');
    const d = await r?.json();
    if (d) {
      if (d.colors) { setColors({ ...DEFAULT_COLORS, ...d.colors }); setSavedColors({ ...DEFAULT_COLORS, ...d.colors }); }
      if (d.font) { setFont(d.font); setSavedFont(d.font); }
      if (d.seo) setSeo(prev => ({ ...prev, ...d.seo }));
    }
  };

  const loadLogo = async () => {
    const r = await apiFetch('/api/settings/logo');
    const d = await r?.json();
    if (d?.path) setLogoPath(d.path);
    const r2 = await apiFetch('/api/settings/logo?usage=pdf');
    const d2 = await r2?.json();
    if (d2?.path) setPdfLogoPath(d2.path);
  };

  const loadSiteUrl = async () => {
    const r = await apiFetch('/api/surveys/site-url');
    const d = await r?.json();
    if (d?.url) setSiteUrl(d.url);
  };

  const saveSiteUrl = async () => {
    setSaving(true);
    await apiFetch('/api/surveys/site-url', { method: 'PUT', body: JSON.stringify({ url: siteUrl }) });
    setSaving(false);
    setFeedback('Live-URL gespeichert');
    setTimeout(() => setFeedback(null), 2000);
  };

  const loadFixNotify = async () => {
    const r = await apiFetch('/api/surveys/fixation-notification-settings');
    const d = await r?.json();
    if (d) setFixNotify({ enabled: d.enabled || false, email: d.email || '' });
  };

  const saveFixNotify = async (data) => {
    setSaving(true);
    await apiFetch('/api/surveys/fixation-notification-settings', { method: 'PUT', body: JSON.stringify(data) });
    setSaving(false);
    setFeedback('Benachrichtigungs-Einstellungen gespeichert');
    setTimeout(() => setFeedback(null), 2000);
  };

  const handleLogoUpload = async (e, usage = 'portal') => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('bgpr_token');
    const r = await fetch(`${API}/api/settings/logo?usage=${usage}`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
    const d = await r.json();
    if (d.path) {
      if (usage === 'pdf') {
        setPdfLogoPath(d.path + '?t=' + Date.now());
        alert('PDF-Logo gespeichert!');
      } else {
        setLogoPath(d.path + '?t=' + Date.now());
        alert('Portal-Logo gespeichert!');
      }
    }
  };

  const showFeedback = (type, text) => {
    setFeedback({ type, text });
    setTimeout(() => setFeedback(null), 4000);
  };

  const colorChange = (key, val) => setColors(prev => ({ ...prev, [key]: val }));

  // --- Colors ---
  const saveColors = async () => {
    setSaving(true);
    const r = await apiFetch('/api/settings/design-config/colors', { method: 'PUT', body: JSON.stringify({ colors }) });
    const d = await r?.json();
    if (d?.status) { setSavedColors({ ...colors }); applyColors(colors); showFeedback('success', 'Farben gespeichert und angewendet.'); }
    setSaving(false);
  };

  const resetColors = async () => {
    setSaving(true);
    const r = await apiFetch('/api/settings/design-config/reset-colors', { method: 'POST' });
    const d = await r?.json();
    if (d?.status) { setColors({ ...DEFAULT_COLORS }); setSavedColors({ ...DEFAULT_COLORS }); applyColors(DEFAULT_COLORS); showFeedback('success', 'Farben auf Standard zurückgesetzt.'); }
    setSaving(false);
  };

  // --- Fonts ---
  const saveFont = async () => {
    setSaving(true);
    const r = await apiFetch('/api/settings/design-config/fonts', { method: 'PUT', body: JSON.stringify({ font }) });
    const d = await r?.json();
    if (d?.status) { setSavedFont(font); applyFont(font); showFeedback('success', 'Schrift gespeichert.'); }
    setSaving(false);
  };

  // --- SEO ---
  const saveSeo = async () => {
    setSaving(true);
    const r = await apiFetch('/api/settings/design-config/seo', { method: 'PUT', body: JSON.stringify(seo) });
    const d = await r?.json();
    if (d?.status) { if (d.seo) setSeo(d.seo); if (onTitleChange && seo.title) onTitleChange(seo.title); showFeedback('success', 'SEO-Einstellungen gespeichert.'); }
    setSaving(false);
  };

  const resetSeo = async () => {
    setSaving(true);
    const r = await apiFetch('/api/settings/design-config/reset-seo', { method: 'POST' });
    const d = await r?.json();
    if (d?.status) { if (d.seo) setSeo(d.seo); if (onTitleChange && d.seo?.title) onTitleChange(d.seo.title); showFeedback('success', 'SEO auf Standard zurückgesetzt.'); }
    setSaving(false);
  };

  const fontFamily = (f) => f === 'system' ? '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' : `'${f}', sans-serif`;

  const tabs = [
    { id: 'allgemein', label: 'Allgemein', icon: <Globe size={15} /> },
    { id: 'farben', label: 'Farben', icon: <Palette size={15} /> },
    { id: 'schriften', label: 'Schriften', icon: <Type size={15} /> },
    { id: 'seo', label: 'SEO & Meta-Tags', icon: <Search size={15} /> },
  ];

  return (
    <div data-testid="design-page">
      <div className="page-header">
        <h2>Design-Einstellungen</h2>
        <p>Passen Sie Farben, Schriften und Meta-Tags Ihrer App an</p>
      </div>

      {/* Tabs */}
      <div className="design-tabs" data-testid="design-tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            className={`design-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
            data-testid={`design-tab-${t.id}`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Feedback */}
      {feedback && (
        <div className={`design-feedback ${feedback.type}`} data-testid="design-feedback">
          {feedback.type === 'success' ? <Check size={14} /> : <AlertTriangle size={14} />}
          {feedback.text}
        </div>
      )}

      {/* ============ ALLGEMEIN TAB ============ */}
      {tab === 'allgemein' && (
        <div className="design-layout">
          <div className="design-main">
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-body">
                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Live-URL</h4>
                <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
                  Die URL Ihrer Live-Anwendung. Wird für QR-Codes auf Zertifikaten und Links in E-Mails verwendet.
                </p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="url"
                    className="form-input"
                    value={siteUrl}
                    onChange={e => setSiteUrl(e.target.value)}
                    placeholder="https://portal.bgpr.de"
                    style={{ flex: 1 }}
                    data-testid="site-url-input"
                  />
                  <button className="btn btn-primary" onClick={saveSiteUrl} disabled={saving} data-testid="save-site-url">
                    <Save size={14} /> Speichern
                  </button>
                </div>
              </div>
            </div>
            {/* Logo sections */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-body">
                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Portal-Logo</h4>
                <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '0 0 8px' }}>Wird in der Sidebar und auf der Login-Seite angezeigt.</p>
                <div className="logo-upload" onClick={() => document.getElementById('logo-input').click()} data-testid="logo-upload-zone">
                  {logoPath && <img src={`${API}${logoPath}`} alt="Logo" style={{ maxHeight: 48, marginBottom: 8 }} />}
                  <span className="btn btn-primary btn-sm"><Upload size={14} /> Logo hochladen</span>
                  <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>PNG, JPG, WebP, SVG (max. 2 MB). Logo wird sofort beim Hochladen gespeichert.</p>
                </div>
                <input id="logo-input" type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleLogoUpload(e, 'portal')} />
              </div>
            </div>
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-body">
                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>PDF-Logo (Branding)</h4>
                <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '0 0 8px' }}>Erscheint oben rechts auf allen PDF-Exporten (Bestandserhebungen, Auswertungen, Rechnungen).</p>
                <div className="logo-upload" onClick={() => document.getElementById('pdf-logo-input').click()} data-testid="pdf-logo-upload-zone">
                  {pdfLogoPath && <img src={`${API}${pdfLogoPath}`} alt="PDF Logo" style={{ maxHeight: 48, marginBottom: 8 }} />}
                  <span className="btn btn-primary btn-sm"><Upload size={14} /> PDF-Logo hochladen</span>
                  <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>PNG, JPG, WebP (max. 2 MB, kein SVG). Logo wird sofort beim Hochladen gespeichert.</p>
                </div>
                <input id="pdf-logo-input" type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={(e) => handleLogoUpload(e, 'pdf')} />
              </div>
            </div>

            {/* Login Background Image */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-body">
                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Login-Hintergrundbild</h4>
                <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '0 0 12px' }}>Optionales Hintergrundbild für die Anmeldeseite. PNG, JPG, WebP (max. 5 MB).</p>
                <LoginBgManager apiFetch={apiFetch} showFeedback={showFeedback} />
              </div>
            </div>

            {/* Fixierungs-Benachrichtigung */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-body">
                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Benachrichtigung bei Fixierung</h4>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                  Wenn aktiviert, erhält die angegebene E-Mail-Adresse bei jeder endgültigen Fixierung einer Bestandsmeldung eine Nachricht mit der Excel-Datei des Anbieters.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <label style={{ fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={fixNotify.enabled}
                      onChange={() => {
                        const updated = { ...fixNotify, enabled: !fixNotify.enabled };
                        setFixNotify(updated);
                        saveFixNotify(updated);
                      }}
                      data-testid="fixation-notify-toggle"
                    />
                    Benachrichtigung aktiviert
                  </label>
                </div>
                {fixNotify.enabled && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="email"
                      className="form-input"
                      value={fixNotify.email}
                      onChange={e => setFixNotify({ ...fixNotify, email: e.target.value })}
                      placeholder="geschaeftsstelle@bgpr.org"
                      style={{ flex: 1, fontSize: 13 }}
                      data-testid="fixation-notify-email"
                    />
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => saveFixNotify(fixNotify)}
                      disabled={saving}
                      data-testid="fixation-notify-save"
                    >
                      <Save size={14} /> Speichern
                    </button>
                  </div>
                )}
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
                  Die Mail-Vorlage kann unter Einstellungen → Mail-Vorlagen → "Geschäftsstelle – Meldung fixiert" angepasst werden.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ FARBEN TAB ============ */}
      {tab === 'farben' && (
        <FarbenTab
          colors={colors}
          setColors={setColors}
          savedColors={savedColors}
          setSavedColors={setSavedColors}
          apiFetch={apiFetch}
          showFeedback={showFeedback}
          saving={saving}
          setSaving={setSaving}
          colorChange={colorChange}
          saveColors={saveColors}
          resetColors={resetColors}
        />
      )}

      {/* ============ SCHRIFTEN TAB ============ */}
      {tab === 'schriften' && (
        <div className="design-layout">
          <div className="design-main">
            <h4 className="design-section-title">Schriftart auswählen</h4>
            <div className="card">
              <div className="card-body">
                <div className="design-info-box" style={{ marginBottom: 16, background: 'rgba(16,185,129,0.08)', borderColor: 'rgba(16,185,129,0.2)', color: '#047857' }}>
                  <Info size={14} />
                  <div><strong>DSGVO-konform</strong><br />Alle Schriften werden <strong>lokal</strong> von Ihrem Server geladen - keine externen Google-Fonts-Aufrufe!</div>
                </div>
                {FONT_OPTIONS.map(f => (
                  <label
                    key={f.id}
                    className={`design-font-option ${font === f.id ? 'active' : ''}`}
                    data-testid={`font-option-${f.id}`}
                  >
                    <input
                      type="radio"
                      name="font"
                      checked={font === f.id}
                      onChange={() => setFont(f.id)}
                      style={{ accentColor: 'var(--primary)' }}
                    />
                    <div>
                      <strong style={{ fontFamily: fontFamily(f.id) }}>{f.label}</strong>
                      {font === f.id && <span className="design-font-badge">Aktiv</span>}
                      <div style={{ fontFamily: fontFamily(f.id), fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{f.sample}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="design-sidebar-panel">
            <div className="card" style={{ position: 'sticky', top: 16 }}>
              <div className="card-body">
                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Vorschau</h4>
                <div style={{ fontFamily: fontFamily(font) }}>
                  <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, fontFamily: fontFamily(font) }}>Überschrift in gewählter Schrift</h3>
                  <p style={{ fontSize: 14, color: 'var(--text-primary)', marginBottom: 8, lineHeight: 1.6 }}>
                    Dies ist ein Beispieltext in der ausgewählten Schriftart. Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.5 }}>
                    Kleinerer Text wird häufig für Beschreibungen und Hinweise verwendet.
                  </p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary btn-sm" style={{ fontFamily: fontFamily(font) }}>Button</button>
                    <button className="btn btn-secondary btn-sm" style={{ fontFamily: fontFamily(font) }}>Sekundär</button>
                  </div>
                </div>
                <div className="design-info-box" style={{ marginTop: 16, background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.2)', color: '#92400E' }}>
                  <AlertTriangle size={14} />
                  <div><strong>Hinweis:</strong> Falls eine Schrift nicht korrekt angezeigt wird, prüfen Sie ob die Schriftdateien im Verzeichnis <code>/assets/fonts/</code> vorhanden sind.</div>
                </div>
                <button className="btn btn-primary" style={{ width: '100%', marginTop: 16 }} onClick={saveFont} disabled={saving || font === savedFont} data-testid="save-font-btn">
                  <Save size={14} /> Schrift speichern
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ SEO TAB ============ */}
      {tab === 'seo' && (
        <div className="design-layout">
          <div className="design-main">
            <h4 className="design-section-title">Meta-Tags</h4>
            <div className="card">
              <div className="card-body">
                <label className="design-form-label">Seitentitel (Title-Tag)</label>
                <input
                  className="form-input"
                  value={seo.title}
                  onChange={e => setSeo(p => ({ ...p, title: e.target.value }))}
                  data-testid="seo-title"
                />
                <p className="design-form-hint">Wird im Browser-Tab und in Suchergebnissen angezeigt</p>

                <label className="design-form-label">Beschreibung (Meta Description)</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={seo.description}
                  onChange={e => setSeo(p => ({ ...p, description: e.target.value }))}
                  data-testid="seo-description"
                  style={{ resize: 'vertical' }}
                />
                <p className="design-form-hint">Kurze Beschreibung für Suchergebnisse (max. 160 Zeichen)</p>

                <label className="design-form-label">Schlüsselwörter (Keywords)</label>
                <input
                  className="form-input"
                  value={seo.keywords}
                  onChange={e => setSeo(p => ({ ...p, keywords: e.target.value }))}
                  data-testid="seo-keywords"
                />
                <p className="design-form-hint">Kommagetrennte Schlüsselwörter (optional)</p>
              </div>
            </div>

            <h4 className="design-section-title" style={{ marginTop: 24 }}>Suchmaschinen-Einstellungen</h4>
            <div className="card">
              <div className="card-body">
                {ROBOTS_OPTIONS.map(opt => (
                  <label
                    key={opt.value}
                    className={`design-font-option ${seo.robots === opt.value ? 'active' : ''}`}
                    data-testid={`robots-${opt.value.replace(/,\s*/g, '-')}`}
                  >
                    <input
                      type="radio"
                      name="robots"
                      checked={seo.robots === opt.value}
                      onChange={() => setSeo(p => ({ ...p, robots: opt.value }))}
                      style={{ accentColor: 'var(--primary)' }}
                    />
                    <div>
                      <strong style={{ fontFamily: 'monospace' }}>{opt.label}</strong>
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{opt.desc}</div>
                    </div>
                  </label>
                ))}
                <div className="design-info-box" style={{ marginTop: 12, background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.2)', color: '#92400E' }}>
                  <AlertTriangle size={14} />
                  <div><strong>Empfehlung:</strong> Für interne Verwaltungsanwendungen sollte "noindex, nofollow" gewählt werden, damit die App nicht in Suchmaschinen erscheint.</div>
                </div>
              </div>
            </div>
          </div>
          <div className="design-sidebar-panel">
            <div className="card" style={{ position: 'sticky', top: 16 }}>
              <div className="card-body">
                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Benutzerdefiniertes CSS (optional)</h4>
                <textarea
                  className="form-input design-css-editor"
                  rows={8}
                  value={seo.customCss}
                  onChange={e => setSeo(p => ({ ...p, customCss: e.target.value }))}
                  placeholder={'/* Eigene CSS-Regeln hier einfügen */\n.custom-class {\n    color: #CC0000;\n}'}
                  data-testid="seo-custom-css"
                />
                <p className="design-form-hint">Eigene CSS-Regeln, die am Ende des Stylesheets eingefügt werden.</p>

                <h4 style={{ fontSize: 14, fontWeight: 700, marginTop: 20, marginBottom: 12 }}>Vorschau: HTML Head</h4>
                <div className="design-html-preview" data-testid="seo-preview">
                  <code>
                    {'<title>'}<span style={{ color: '#10B981' }}>{seo.title}</span>{'</title>'}{'\n'}
                    {'<meta name="description" content="'}<span style={{ color: '#10B981' }}>{seo.description}</span>{'">'}{'\n'}
                    {'<meta name="keywords" content="'}<span style={{ color: '#10B981' }}>{seo.keywords}</span>{'">'}{'\n'}
                    {'<meta name="robots" content="'}<span style={{ color: '#10B981' }}>{seo.robots}</span>{'">'}
                  </code>
                </div>

                <button className="btn btn-primary" style={{ width: '100%', marginTop: 16 }} onClick={saveSeo} disabled={saving} data-testid="save-seo-btn">
                  <Save size={14} /> Meta-Tags speichern
                </button>
                <button className="design-reset-link" onClick={resetSeo} disabled={saving} data-testid="reset-seo-btn">
                  <RotateCcw size={13} /> Auf Standard zurücksetzen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ============ FARBEN TAB COMPONENT ============ */
function FarbenTab({ colors, setColors, savedColors, setSavedColors, apiFetch, showFeedback, saving, setSaving, colorChange, saveColors, resetColors }) {
  const [presets, setPresets] = useState([]);
  const [activePreset, setActivePreset] = useState(null);
  const [showNewPreset, setShowNewPreset] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const importRef = useRef(null);

  useEffect(() => { loadPresets(); }, []);

  const loadPresets = async () => {
    const r = await apiFetch('/api/settings/design-presets');
    const d = await r?.json();
    if (Array.isArray(d)) setPresets(d);
  };

  const applyPreset = (preset) => {
    const merged = { ...DEFAULT_COLORS, ...preset.colors };
    setColors(merged);
    setActivePreset(preset.id);
    applyColors(merged);
  };

  const saveAsPreset = async () => {
    if (!newPresetName.trim()) return;
    const r = await apiFetch('/api/settings/design-presets', {
      method: 'POST', body: JSON.stringify({ name: newPresetName.trim(), colors })
    });
    const d = await r?.json();
    if (d?.status) {
      showFeedback('success', `Vorlage "${newPresetName}" gespeichert`);
      setNewPresetName('');
      setShowNewPreset(false);
      loadPresets();
    }
  };

  const duplicatePreset = async (preset) => {
    const r = await apiFetch('/api/settings/design-presets', {
      method: 'POST', body: JSON.stringify({ name: `${preset.name} (Kopie)`, colors: preset.colors })
    });
    const d = await r?.json();
    if (d?.status) {
      showFeedback('success', 'Vorlage dupliziert');
      loadPresets();
    }
  };

  const deletePreset = async (preset) => {
    if (!window.confirm(`Vorlage "${preset.name}" wirklich löschen?`)) return;
    const r = await apiFetch(`/api/settings/design-presets/${preset.id}`, { method: 'DELETE' });
    const d = await r?.json();
    if (d?.status) {
      showFeedback('success', 'Vorlage gelöscht');
      if (activePreset === preset.id) setActivePreset(null);
      loadPresets();
    } else {
      showFeedback('error', d?.detail || 'Fehler');
    }
  };

  const exportColors = () => {
    const data = JSON.stringify({ _type: 'bgpr_design_colors', _version: 1, colors }, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bgpr-design-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showFeedback('success', 'Design als JSON exportiert');
  };

  const importColors = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        const imported = data.colors || data;
        if (typeof imported !== 'object' || !imported.primary) {
          showFeedback('error', 'Ungültige Datei – kein gültiges Farbschema gefunden');
          return;
        }
        const merged = { ...DEFAULT_COLORS, ...imported };
        setColors(merged);
        applyColors(merged);
        setActivePreset(null);
        showFeedback('success', 'Farbschema importiert – bitte noch speichern!');
      } catch {
        showFeedback('error', 'Datei konnte nicht gelesen werden');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Determine preview colors for preset circles
  const getPresetPreviewColors = (preset) => {
    const c = preset.colors || {};
    return [c.primary || '#CC0000', c.sidebarBg || '#1F2937', c.sidebarActive || '#CC0000'];
  };

  return (
    <div className="design-layout">
      <div className="design-main">
        {/* Presets Section */}
        <div className="design-section">
          <h4 className="design-section-title">Farbvorlagen</h4>
          <div className="card">
            <div className="card-body">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                {presets.map(p => (
                  <div key={p.id} data-testid={`preset-${p.id}`}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer',
                      padding: '8px 12px', borderRadius: 8, border: activePreset === p.id ? '2px solid var(--primary)' : '2px solid transparent',
                      background: activePreset === p.id ? 'rgba(204,0,0,0.04)' : 'transparent', transition: 'all 0.15s',
                      minWidth: 80,
                    }}
                    onClick={() => applyPreset(p)}
                  >
                    <div style={{ display: 'flex', gap: 2 }}>
                      {getPresetPreviewColors(p).map((c, i) => (
                        <div key={i} style={{ width: 18, height: 18, borderRadius: '50%', background: c, border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }} />
                      ))}
                    </div>
                    <span style={{ fontSize: 11, fontWeight: activePreset === p.id ? 700 : 500, color: 'var(--text-primary)', textAlign: 'center', lineHeight: 1.2, maxWidth: 90 }}>{p.name}</span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="design-preset-action" title="Duplizieren" data-testid={`preset-dup-${p.id}`}
                        onClick={e => { e.stopPropagation(); duplicatePreset(p); }}>
                        <Copy size={11} />
                      </button>
                      {!p.is_default && (
                        <button className="design-preset-action" title="Löschen" data-testid={`preset-del-${p.id}`}
                          onClick={e => { e.stopPropagation(); deletePreset(p); }}
                          style={{ color: 'var(--error)' }}>
                          <Trash2 size={11} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Import / Export / Save as Preset */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', borderTop: '1px solid var(--border-color, #e5e7eb)', paddingTop: 12 }}>
                {!showNewPreset ? (
                  <button className="btn btn-sm" onClick={() => setShowNewPreset(true)} data-testid="save-as-preset-btn"
                    style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Plus size={13} /> Als Vorlage speichern
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 1 }}>
                    <input
                      className="form-input"
                      value={newPresetName}
                      onChange={e => setNewPresetName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && saveAsPreset()}
                      placeholder="Name der Vorlage"
                      autoFocus
                      style={{ fontSize: 12, padding: '4px 8px', flex: 1, maxWidth: 200 }}
                      data-testid="new-preset-name"
                    />
                    <button className="btn btn-primary btn-sm" onClick={saveAsPreset} style={{ fontSize: 12 }} data-testid="save-preset-confirm">
                      <Save size={12} /> Speichern
                    </button>
                    <button className="btn btn-sm" onClick={() => { setShowNewPreset(false); setNewPresetName(''); }} style={{ fontSize: 12 }}>
                      Abbrechen
                    </button>
                  </div>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  <button className="btn btn-sm" onClick={exportColors} data-testid="export-colors-btn"
                    style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Download size={13} /> Export (JSON)
                  </button>
                  <button className="btn btn-sm" onClick={() => importRef.current?.click()} data-testid="import-colors-btn"
                    style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FileUp size={13} /> Import (JSON)
                  </button>
                  <input ref={importRef} type="file" accept=".json" style={{ display: 'none' }} onChange={importColors} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Color Sections */}
        {COLOR_SECTIONS.map(section => (
          <div key={section.title} className="design-section">
            <h4 className="design-section-title">{section.title}</h4>
            <div className="card">
              <div className="card-body" style={{ padding: 0 }}>
                {section.items.map(item => (
                  <ColorRow
                    key={item.key}
                    item={item}
                    value={colors[item.key] || '#000000'}
                    defaultValue={DEFAULT_COLORS[item.key]}
                    onChange={(key, val) => { colorChange(key, val); setActivePreset(null); }}
                  />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Sidebar */}
      <div className="design-sidebar-panel">
        <div className="card" style={{ position: 'sticky', top: 16 }}>
          <div className="card-body">
            <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Live-Vorschau</h4>
            <LivePreviewEnhanced colors={colors} />
            <button className="btn btn-primary" style={{ width: '100%', marginTop: 16 }} onClick={saveColors} disabled={saving} data-testid="save-colors-btn">
              <Save size={14} /> Farben speichern
            </button>
            <button className="design-reset-link" onClick={resetColors} disabled={saving} data-testid="reset-colors-btn">
              <RotateCcw size={13} /> Alle zurücksetzen
            </button>
            <div className="design-info-box">
              <Info size={14} />
              Die Farben werden nach dem Speichern sofort in der gesamten App angewendet.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============ ENHANCED LIVE PREVIEW ============ */
function LivePreviewEnhanced({ colors }) {
  return (
    <div data-testid="live-preview-enhanced">
      <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb', marginBottom: 12 }}>
        <div style={{ display: 'flex', height: 180 }}>
          {/* Sidebar preview */}
          <div style={{ width: 56, background: colors.sidebarBg, padding: '8px 4px', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: colors.primary, margin: '0 auto 6px', opacity: 0.7 }} />
            <div style={{ height: 7, borderRadius: 3, background: colors.sidebarActive, margin: '0 3px' }} />
            <div style={{ height: 7, borderRadius: 3, background: colors.sidebarText, opacity: 0.3, margin: '0 3px' }} />
            <div style={{ height: 7, borderRadius: 3, background: colors.sidebarText, opacity: 0.3, margin: '0 3px' }} />
            <div style={{ height: 7, borderRadius: 3, background: colors.sidebarText, opacity: 0.3, margin: '0 3px' }} />
          </div>
          {/* Content area */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ height: 24, background: colors.headerBg, borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', padding: '0 8px' }}>
              <div style={{ height: 5, width: 50, background: colors.textPrimary, borderRadius: 2, opacity: 0.5 }} />
            </div>
            {/* Page */}
            <div style={{ flex: 1, background: colors.pageBg, padding: 8 }}>
              {/* Card */}
              <div style={{ background: colors.cardBg, borderRadius: 5, padding: 8, marginBottom: 6, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <div style={{ height: 5, width: '50%', background: colors.textPrimary, borderRadius: 2, marginBottom: 4 }} />
                <div style={{ height: 4, width: '75%', background: colors.textSecondary, borderRadius: 2, marginBottom: 8, opacity: 0.6 }} />
                <div style={{ display: 'flex', gap: 4 }}>
                  <div style={{ height: 16, width: 40, background: colors.btnPrimaryBg, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 6, color: colors.btnPrimaryText, fontWeight: 700 }}>Aktion</span>
                  </div>
                  <div style={{ height: 16, width: 40, background: colors.btnEditBg, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 6, color: colors.btnEditText, fontWeight: 700 }}>Edit</span>
                  </div>
                  <div style={{ height: 16, width: 40, background: colors.btnDangerBg, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 6, color: colors.btnDangerText, fontWeight: 700 }}>Löschen</span>
                  </div>
                </div>
              </div>
              {/* Status indicators */}
              <div style={{ display: 'flex', gap: 4 }}>
                <div style={{ flex: 1, height: 20, background: colors.success, borderRadius: 4, opacity: 0.25, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 6, color: colors.success, fontWeight: 700 }}>Erfolg</span>
                </div>
                <div style={{ flex: 1, height: 20, background: colors.warning, borderRadius: 4, opacity: 0.25, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 6, color: colors.warning, fontWeight: 700 }}>Warnung</span>
                </div>
                <div style={{ flex: 1, height: 20, background: colors.error, borderRadius: 4, opacity: 0.25, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 6, color: colors.error, fontWeight: 700 }}>Fehler</span>
                </div>
                <div style={{ flex: 1, height: 20, background: colors.info, borderRadius: 4, opacity: 0.25, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 6, color: colors.info, fontWeight: 700 }}>Info</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Color legend */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
        {[
          { label: 'Primär', color: colors.primary },
          { label: 'Sidebar', color: colors.sidebarBg },
          { label: 'Seite', color: colors.pageBg },
          { label: 'Karten', color: colors.cardBg },
        ].map(item => (
          <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: 'var(--text-secondary)' }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: item.color, border: '1px solid rgba(0,0,0,0.1)', flexShrink: 0 }} />
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
}


/* ============ LOGIN BACKGROUND MANAGER ============ */
function LoginBgManager({ apiFetch, showFeedback }) {
  const [bgPath, setBgPath] = useState(null);
  const [overlay, setOverlay] = useState('#1a2035');
  const [opacity, setOpacity] = useState(0.6);
  const [blur, setBlur] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    // Load current bg
    fetch(`${API}/api/settings/login-bg`).then(r => r.json()).then(d => { if (d?.path) setBgPath(d.path); }).catch(() => {});
    // Load config
    fetch(`${API}/api/settings/design-config/public`).then(r => r.json()).then(d => {
      if (d) {
        if (d.login_bg_overlay) setOverlay(d.login_bg_overlay);
        if (d.login_bg_opacity !== undefined) setOpacity(d.login_bg_opacity);
        if (d.login_bg_blur !== undefined) setBlur(d.login_bg_blur);
      }
    }).catch(() => {});
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('bgpr_token');
      const res = await fetch(`${API}/api/settings/login-bg`, {
        method: 'POST', body: formData, headers: { 'Authorization': `Bearer ${token}` }
      });
      const d = await res.json();
      if (d?.status) {
        setBgPath(d.path + '?t=' + Date.now());
        showFeedback('success', 'Hintergrundbild hochgeladen');
      } else {
        showFeedback('error', d?.detail || 'Upload fehlgeschlagen');
      }
    } catch { showFeedback('error', 'Upload fehlgeschlagen'); }
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async () => {
    if (!window.confirm('Hintergrundbild entfernen?')) return;
    const r = await apiFetch('/api/settings/login-bg', { method: 'DELETE' });
    const d = await r?.json();
    if (d?.status) { setBgPath(null); showFeedback('success', 'Hintergrundbild entfernt'); }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    const r = await apiFetch('/api/settings/login-bg/config', {
      method: 'PUT', body: JSON.stringify({ overlay, opacity, blur })
    });
    const d = await r?.json();
    if (d?.status) showFeedback('success', 'Einstellungen gespeichert');
    setSavingConfig(false);
  };

  return (
    <div data-testid="login-bg-manager">
      {/* Preview */}
      <div style={{
        position: 'relative', borderRadius: 8, overflow: 'hidden', marginBottom: 12,
        height: 140, background: bgPath ? 'none' : 'linear-gradient(135deg, #1a2035, #2d3748)',
        border: '1px solid var(--border-color, #e5e7eb)',
      }}>
        {bgPath && (
          <>
            <img src={`${API}${bgPath.split('?')[0]}?t=${Date.now()}`} alt="Login BG"
              style={{ width: '100%', height: '100%', objectFit: 'cover', filter: blur > 0 ? `blur(${blur}px)` : 'none' }}
            />
            <div style={{
              position: 'absolute', inset: 0, background: overlay, opacity,
            }} />
          </>
        )}
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6 }}>
            <Heart size={16} color="#fff" fill="#fff" />
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>Login-Vorschau</div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>Bitte melden Sie sich an</div>
        </div>
      </div>

      {/* Upload / Remove */}
      <div style={{ display: 'flex', gap: 8, marginBottom: bgPath ? 16 : 0 }}>
        <button className="btn btn-sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="login-bg-upload-btn"
          style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
          <Upload size={13} /> {uploading ? 'Hochladen...' : (bgPath ? 'Bild ändern' : 'Bild hochladen')}
        </button>
        {bgPath && (
          <button className="btn btn-sm" onClick={handleDelete} data-testid="login-bg-delete-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--error)' }}>
            <Trash2 size={13} /> Entfernen
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={handleUpload} />
      </div>

      {/* Overlay settings - only when bg is set */}
      {bgPath && (
        <div style={{ borderTop: '1px solid var(--border-color, #e5e7eb)', paddingTop: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, display: 'block', marginBottom: 4 }}>Overlay-Farbe</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="color" value={overlay} onChange={e => setOverlay(e.target.value)} data-testid="login-bg-overlay-color"
                  style={{ width: 32, height: 28, border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', padding: 1 }} />
                <input className="form-input" value={overlay} onChange={e => setOverlay(e.target.value)}
                  style={{ fontSize: 11, padding: '3px 6px', fontFamily: 'monospace' }} data-testid="login-bg-overlay-hex" />
              </div>
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                Deckkraft: {Math.round(opacity * 100)}%
              </label>
              <input type="range" min="0" max="1" step="0.05" value={opacity} onChange={e => setOpacity(parseFloat(e.target.value))} data-testid="login-bg-opacity-slider"
                style={{ width: '100%', accentColor: 'var(--primary)' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                Unschärfe: {blur}px
              </label>
              <input type="range" min="0" max="20" step="1" value={blur} onChange={e => setBlur(parseInt(e.target.value))} data-testid="login-bg-blur-slider"
                style={{ width: '100%', accentColor: 'var(--primary)' }} />
            </div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={saveConfig} disabled={savingConfig} data-testid="login-bg-save-config"
            style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
            <Save size={13} /> Overlay-Einstellungen speichern
          </button>
        </div>
      )}
    </div>
  );
}
